import { type User } from "@overmap-ai/common"
import { selectUserById, selectUsersByIds } from "@overmap-ai/core"

import { appStore } from "../../store"
import { FilterModifier } from "./FilterModifier"

const deserializeOptionalUserId = (filterValue: User["id"] | null) => {
	if (filterValue) {
		const user = selectUserById(filterValue)(appStore.getState())

		return user?.id
	}

	return null
}

export const OptionalUserEqualsFilterModifier = FilterModifier.create<User["id"] | null, User["id"] | null>({
	id: "optional-id-equals",
	getApplyFn: (filterValue) => (value) => {
		return value === filterValue
	},
	render: () => "is",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeOptionalUserId,
})

export const OptionalUserNotEqualsFilterModifier = FilterModifier.create<User["id"] | null, User["id"] | null>({
	id: "optional-id-not-equals",
	getApplyFn: (filterValue) => (value) => {
		return value !== filterValue
	},
	render: () => "is not",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeOptionalUserId,
})

const deserializeOptionalUserIds = (filterValue: (User["id"] | null)[]) => {
	const userIds = filterValue.filter((userId) => userId !== null)
	const users = selectUsersByIds(userIds)(appStore.getState())

	const foundUserIds: (User["id"] | null)[] = users.map((user) => user?.id)

	if (filterValue.includes(null)) foundUserIds.push(null)

	return foundUserIds.length > 0 ? foundUserIds : undefined
}

export const OptionalUserIncludesFilterModifier = FilterModifier.create<User["id"] | null, (User["id"] | null)[]>({
	id: "optional-id-includes",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			return filterValueSet.has(value)
		}
	},
	render: () => "is any of",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeOptionalUserIds,
})

export const OptionalUserExcludesFilterModifier = FilterModifier.create<User["id"] | null, (User["id"] | null)[]>({
	id: "optional-id-excludes",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			return !filterValueSet.has(value)
		}
	},
	render: () => "is none of",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeOptionalUserIds,
})

const deserializeUserId = (filterValue: User["id"]) => {
	const user = selectUserById(filterValue)(appStore.getState())

	return user?.id
}

export const UserEqualsFilterModifier = FilterModifier.create<User["id"], User["id"]>({
	id: "id-equals",
	getApplyFn: (filterValue) => (value) => {
		return value === filterValue
	},
	render: () => "is",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeUserId,
})

export const UserNotEqualsFilterModifier = FilterModifier.create<User["id"], User["id"]>({
	id: "id-not-equals",
	getApplyFn: (filterValue) => (value) => {
		return value !== filterValue
	},
	render: () => "is not",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeUserId,
})

const deserializeUserIds = (filterValue: User["id"][]) => {
	const users = selectUsersByIds(filterValue)(appStore.getState())

	const foundUserIds = users.map((user) => user.id)

	return foundUserIds.length > 0 ? foundUserIds : undefined
}

export const UserIncludesFilterModifier = FilterModifier.create<User["id"], User["id"][]>({
	id: "id-includes",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			return filterValueSet.has(value)
		}
	},
	render: () => "is any of",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeUserIds,
})

export const UserExcludesFilterModifier = FilterModifier.create<User["id"], User["id"][]>({
	id: "id-excludes",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			return !filterValueSet.has(value)
		}
	},
	render: () => "is none of",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeUserIds,
})

export const UserArrayEqualsFilterModifier = FilterModifier.create<User["id"][], User["id"][]>({
	id: "user-array-equals",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			const valueSet = new Set(value)
			return valueSet.size === filterValueSet.size && filterValueSet.isSubsetOf(valueSet)
		}
	},
	render: () => "is",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeUserIds,
})

export const UserArrayNotEqualsFilterModifier = FilterModifier.create<User["id"][], User["id"][]>({
	id: "user-array-not-equals",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			const valueSet = new Set(value)
			return valueSet.size !== filterValueSet.size || !filterValueSet.isSubsetOf(valueSet)
		}
	},
	render: () => "is not",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeUserIds,
})

export const UserArrayEveryFilterModifier = FilterModifier.create<User["id"][], User["id"][]>({
	id: "user-array-every",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			const valueSet = new Set(value)
			return filterValueSet.isSubsetOf(valueSet)
		}
	},
	render: () => "has all of",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeUserIds,
})

export const UserArraySomeFilterModifier = FilterModifier.create<User["id"][], User["id"][]>({
	id: "user-array-some",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			const valueSet = new Set(value)
			return !filterValueSet.isDisjointFrom(valueSet)
		}
	},
	render: () => "has any of",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeUserIds,
})

export const UserArrayNoneFilterModifier = FilterModifier.create<User["id"][], User["id"][]>({
	id: "user-array-none",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			const valueSet = new Set(value)
			return filterValueSet.isDisjointFrom(valueSet)
		}
	},
	render: () => "has none of",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeUserIds,
})
